import React from "react";
import styled, { keyframes } from "styled-components/macro";

/*
 * Loading screen of File Split. It mirrors the real layout (PdfViewer/index.js): the Split Details
 * panel at the same width, the toolbar and the page grid, so nothing jumps when the file opens.
 * A status card over the page grid shows the download progress, then "Preparing pages".
 *
 * pdfLoaded   - true while loading (the screen is shown)
 * progress    - download progress, 0..100
 * downloading - true until the PDF is downloaded; without it, progress < 100 counts as downloading
 * leftWidth   - Split Details width in % (0 when the panel is hidden)
 */
const LEFT_PAGE_CARDS = 9;
const GRID_PAGES = 32;
const PAGE_LINES = [70, 90, 55, 85, 60, 80];

const FileSPlitSkeleton = ({ pdfLoaded, progress, downloading, leftWidth = 20 }) => {
  const percent = Math.min(100, Math.max(0, Math.floor(Number(progress) || 0)));
  const isDownloading = downloading ?? percent < 100;
  const showPercent = isDownloading && percent > 0;
  const title = isDownloading ? "Downloading file" : "Preparing pages";
  const hint = isDownloading
    ? percent > 0
      ? "Large files can take a minute"
      : "Starting download"
    : "Building page previews";

  return (
    <SkeletonRoot $visible={pdfLoaded} aria-busy="true">
      {leftWidth > 0 && (
        <>
          <LeftPanel style={{ width: `${leftWidth}%` }} aria-hidden="true">
            <PanelCard>
              <div className="panel-title-row">
                <Bone $w="34px" $h="34px" $r="9px" />
                <Bone $w="110px" $h="16px" />
                <Bone $w="30px" $h="30px" $r="8px" className="push-right" />
              </div>
              <div className="panel-progress">
                <Bone $w="96px" $h="10px" />
                <Bone $w="64px" $h="14px" />
              </div>
              <Bone $w="100%" $h="6px" $r="3px" />
              <div className="panel-divider" />
              <div className="panel-pages">
                {Array.from({ length: LEFT_PAGE_CARDS }, (_, i) => (
                  <div className="page-card" key={i}>
                    <Bone className="page-card-thumb" $w="100%" $r="4px" />
                    <div className="page-card-footer">
                      <Bone $w="22px" $h="18px" $r="9px" />
                      <Bone $w="14px" $h="14px" $r="4px" />
                      <Bone $w="24px" $h="10px" />
                    </div>
                  </div>
                ))}
              </div>
            </PanelCard>
          </LeftPanel>
          <Divider aria-hidden="true" />
        </>
      )}

      <RightPanel>
        <Toolbar aria-hidden="true">
          <div className="tool-group"><Bone $w="34px" $h="34px" $r="8px" /></div>
          <Bone $w="88px" $h="34px" $r="8px" />
          <div className="tool-group"><Bone $w="34px" $h="34px" $r="8px" /><Bone $w="34px" $h="34px" $r="8px" /></div>
          <div className="tool-group"><Bone $w="34px" $h="34px" $r="8px" /><Bone $w="34px" $h="34px" $r="8px" /></div>
          <div className="tool-group"><Bone $w="34px" $h="34px" $r="8px" /><Bone $w="34px" $h="34px" $r="8px" /></div>
          <Bone $w="136px" $h="40px" $r="10px" />
          <Bone $w="76px" $h="34px" $r="8px" $accent />
          <div className="tool-actions">
            <Bone $w="136px" $h="40px" $r="10px" />
            <div className="tool-group"><Bone $w="34px" $h="34px" $r="8px" /></div>
          </div>
        </Toolbar>

        <PageArea>
          <div className="page-grid" aria-hidden="true">
            {Array.from({ length: GRID_PAGES }, (_, i) => (
              <div className="grid-page" key={i}>
                <Bone className="grid-page-number" $w="18px" $h="12px" />
                <div className="grid-page-frame">
                  <div className="grid-page-sheet">
                    <div className="sheet-head">
                      <Bone $w="28%" $h="7px" $r="2px" />
                      <Bone $w="34%" $h="12px" $r="2px" />
                    </div>
                    {PAGE_LINES.map((w, line) => (
                      <Bone key={line} $w={`${w}%`} $h="5px" $r="2px" />
                    ))}
                    <Bone className="sheet-table" $w="100%" $r="2px" />
                  </div>
                  <span className="grid-page-check" />
                </div>
              </div>
            ))}
          </div>

          <StatusCard>
            <span className={`status-icon${isDownloading ? "" : " is-preparing"}`} aria-hidden="true">
              <i className={`bi ${isDownloading ? "bi-cloud-arrow-down" : "bi-layers"}`} />
            </span>
            <div className="status-text">
              <div className="status-title-row">
                <span className="status-title" aria-live="polite">{title}</span>
                {showPercent && <span className="status-percent">{percent}%</span>}
              </div>
              <span className="status-hint">{hint}</span>
              <div
                className="status-bar"
                role="progressbar"
                aria-label={title}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={showPercent ? percent : undefined}
              >
                {showPercent ? (
                  <span className="status-bar-fill" style={{ width: `${percent}%` }} />
                ) : (
                  <span className="status-bar-indeterminate" />
                )}
              </div>
            </div>
          </StatusCard>
        </PageArea>
      </RightPanel>
    </SkeletonRoot>
  );
};

export default FileSPlitSkeleton;

/* ─── Styles ────────────────────────────────────────────────────────────── */
const ACCENT = "#036b87";
const BORDER = "#e3e8eb";
const BONE = "#edf1f3";

/*
 * One light band sweeps across the whole screen (SkeletonRoot::after), so all bones shimmer
 * together. It moves with transform only: no repaint of the bones on every frame while the
 * PDF is loading.
 */
const sweep = keyframes`
  from { transform: translateX(-100%); }
  to { transform: translateX(100%); }
`;
const slide = keyframes`
  from { transform: translateX(-100%); }
  to { transform: translateX(250%); }
`;
const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(3, 107, 135, 0.25); }
  50% { box-shadow: 0 0 0 8px rgba(3, 107, 135, 0); }
`;
const fadeIn = keyframes`
  from { opacity: 0; transform: translate(-50%, 8px); }
  to { opacity: 1; transform: translate(-50%, 0); }
`;

const Bone = styled.span`
  display: block;
  flex: none;
  width: ${(p) => p.$w || "100%"};
  height: ${(p) => p.$h || "auto"};
  border-radius: ${(p) => p.$r || "4px"};
  background: ${(p) => (p.$accent ? "rgba(3, 107, 135, 0.16)" : BONE)};
`;

/* Same box as MainDivContainer in PdfViewer/index.js. */
const SkeletonRoot = styled.div`
  position: relative;
  display: ${(p) => (p.$visible ? "flex" : "none")};
  margin-top: 10px;
  padding-left: 20px;
  overflow: hidden;
  user-select: none;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    z-index: 1;
    pointer-events: none;
    background: linear-gradient(100deg, transparent 35%, rgba(255, 255, 255, 0.65) 50%, transparent 65%);
    transform: translateX(-100%);
    animation: ${sweep} 1.8s ease-in-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    &::after {
      display: none;
    }
  }
`;

const LeftPanel = styled.div`
  flex: none;
`;

/* Same box as BottomClaimContainer. */
const PanelCard = styled.div`
  height: calc(85vh + 64px);
  overflow: hidden;
  background: #fff;
  border: 1px solid ${BORDER};
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.06);
  padding: 14px 14px 10px;

  .panel-title-row {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .push-right {
    margin-left: auto;
  }
  .panel-progress {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 16px 0 7px;
  }
  .panel-divider {
    height: 1px;
    margin: 14px -14px 14px;
    background: #eef2f4;
  }
  .panel-pages {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 14px 10px;
  }
  .page-card {
    padding: 5px;
    border-radius: 6px;
    box-shadow: 0 0 0 1px #eef2f4, 0 1px 3px rgba(16, 24, 40, 0.06);
  }
  .page-card-thumb {
    aspect-ratio: 4 / 5;
  }
  .page-card-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 8px;
  }
`;

/* Same place as ResizeLine. */
const Divider = styled.div`
  flex: none;
  border-right: 3px solid #fff;
  box-shadow: rgb(232, 232, 232) 4px 4px 0px 0px;
`;

const RightPanel = styled.div`
  flex: 1;
  min-width: 0;
`;

/* Same box as ButtonIconConatiner inside TopIconAndPaginateConatiner. */
const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  min-height: 54px;
  margin: 0 10px 10px 0;
  padding: 6px 10px;
  background: #fff;
  border: 1px solid ${BORDER};
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.06);

  .tool-group {
    display: flex;
    gap: 2px;
    padding: 3px;
    border-radius: 10px;
    background: #f6f8f9;
  }
  .tool-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: auto;
  }
`;

const PageArea = styled.div`
  position: relative;
  height: 85vh;
  overflow: hidden;

  .page-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 22px 25px;
    padding: 12px 10px 0 15px;
    /* Pages fade out towards the bottom, behind the status card. */
    -webkit-mask-image: linear-gradient(to bottom, #000 45%, transparent 100%);
    mask-image: linear-gradient(to bottom, #000 45%, transparent 100%);
  }
  .grid-page {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }
  .grid-page-frame {
    position: relative;
    width: 100%;
    aspect-ratio: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    box-shadow: 0 0 0 1px #eef2f4;
  }
  .grid-page-sheet {
    width: 74%;
    height: 96%;
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 8% 8% 6%;
    background: #fff;
    border: 1px solid #e6ebee;
    border-radius: 2px;
  }
  .sheet-head {
    display: flex;
    justify-content: space-between;
    margin-bottom: 4px;
  }
  .sheet-table {
    flex: 1;
    margin-top: 4px;
  }
  .grid-page-check {
    position: absolute;
    right: 4px;
    bottom: 4px;
    width: 12px;
    height: 12px;
    border: 1px solid #d5dde1;
    border-radius: 3px;
    background: #fff;
  }
`;

/* Above the shimmer band (z-index 1). */
const StatusCard = styled.div`
  position: absolute;
  z-index: 2;
  top: 34%;
  left: 50%;
  width: min(360px, calc(100% - 32px));
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 18px;
  background: #fff;
  border: 1px solid ${BORDER};
  border-radius: 14px;
  box-shadow: 0 12px 32px rgba(16, 24, 40, 0.12), 0 2px 6px rgba(16, 24, 40, 0.06);
  transform: translate(-50%, 0);
  animation: ${fadeIn} 0.3s ease;

  .status-icon {
    flex: none;
    width: 44px;
    height: 44px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(3, 107, 135, 0.1);
    color: ${ACCENT};
    font-size: 20px;
    animation: ${pulse} 2s ease-in-out infinite;
  }
  .status-text {
    flex: 1;
    min-width: 0;
  }
  .status-title-row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
  }
  .status-title {
    font-size: 14.5px;
    font-weight: 700;
    color: #1f2d33;
  }
  .status-percent {
    font-size: 14px;
    font-weight: 700;
    color: ${ACCENT};
    font-variant-numeric: tabular-nums;
  }
  .status-hint {
    display: block;
    margin-top: 2px;
    font-size: 12px;
    color: #6b7a81;
  }
  .status-bar {
    position: relative;
    height: 6px;
    margin-top: 10px;
    border-radius: 3px;
    background: #e8eef1;
    overflow: hidden;
  }
  .status-bar-fill {
    display: block;
    height: 100%;
    border-radius: 3px;
    background: linear-gradient(90deg, #0a8fb0, ${ACCENT});
    transition: width 0.3s ease;
  }
  .status-bar-indeterminate {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 40%;
    border-radius: 3px;
    background: linear-gradient(90deg, #0a8fb0, ${ACCENT});
    animation: ${slide} 1.2s ease-in-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    .status-icon,
    .status-bar-indeterminate {
      animation: none;
    }
  }
`;
